import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { markRouteChangeStart, resetBackNavigationSoon } from '../utils/navigation-direction';

interface TransitionOptions {
  shallow?: boolean;
}

interface RouterContextType {
  pathname: string;
  asPath: string;
  query: Record<string, string>;
  push: (url: string, options?: TransitionOptions) => void;
  replace: (url: string, options?: TransitionOptions) => void;
  /**
   * Warm a route's JS bundle + SSG/ISR data before navigation. Used for
   * hover-intent prefetching of product pages so the click lands on an
   * already-generated page instead of an on-demand `fallback: true` render.
   * No-op in dev (Next disables prefetch) and outside a provider.
   */
  prefetch: (url: string) => void;
  isReady: boolean;
}

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export const useRouterContext = (): RouterContextType => {
  // eslint-disable-next-line sellwild-code/context-throws-outside-provider
  const context = useContext(RouterContext);

  if (!context) {
    return {
      pathname: '/',
      asPath: '/',
      query: {},
      push: () => { /* noop */ },
      replace: () => { /* noop */ },
      prefetch: () => { /* noop */ },
      isReady: false,
    };
  }

  return context;
};

interface RouterProviderProps {
  children: ReactNode;
  /** Override the seed path. Tests use it; the app leaves it unset. */
  initialPath?: string;
}

/**
 * SSG-safe router context provider
 * Provides routing information that works during SSG and client-side
 */
export const RouterProvider: React.FC<RouterProviderProps> = ({
  children,
  initialPath,
}) => {
  const nextRouter = useRouter();

  // SEEDED FROM THE ROUTER, NOT FROM '/'.
  //
  // These started at a hardcoded '/', and the effect below only corrected them
  // after mount. So the server render and the entire first client render of
  // EVERY page believed it was on the home page, and two things downstream
  // believed it too:
  //
  // - all-layout renders a second, `position: fixed` copy of the product page
  //   (`.product-overlay`) whenever a product is open and the path is not a
  //   /product/ route. '/' is not, so a product page server-rendered ITSELF
  //   TWICE -- two `product-page` nodes and two headers in one document -- and
  //   the copy on top is capped by `.product-modal { max-width: 1400px }`. On a
  //   1600px screen the reader got the page 100px narrower on each side until
  //   hydration threw that copy away, which is the "it pops out on both sides".
  // - `motion.main` is keyed on this pathname, so correcting it changed the key
  //   and AnimatePresence swapped <main> for a brand new element: the whole
  //   page subtree remounted on load. Measured on prod, that single shift was
  //   CLS 0.901 on every page except '/' (which only escaped because its path
  //   is the seed).
  //
  // `nextRouter.pathname` is the route pattern and `nextRouter.asPath` the URL;
  // both read the same on the server and on the client's first render, so
  // seeding from them costs no hydration mismatch. `query` and `isReady` still
  // wait for the router -- those genuinely are not known before it is ready.
  const [pathname, setPathname] = useState(initialPath ?? nextRouter.pathname);
  const [asPath, setAsPath] = useState(initialPath ?? nextRouter.asPath);
  const [query, setQuery] = useState<Record<string, string>>({});
  const [isReady, setIsReady] = useState(false);

  // Sync with Next.js router - initial setup and event listeners
  useEffect(() => {
    if (!nextRouter.isReady) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local SSG-safe router state with the external Next.js router; setIsReady(false) reflects the router not yet being ready and is part of external-system synchronization, not derivable during render
      setIsReady(false);
      return;
    }

    const updateFromNextRouter = () => {
      setPathname(nextRouter.pathname);
      setAsPath(nextRouter.asPath);

      // Convert Next.js query object to string-only query
      const queryObj: Record<string, string> = {};
      Object.entries(nextRouter.query).forEach(([key, value]) => {
        if (typeof value === 'string') {
          queryObj[key] = value;
        } else if (Array.isArray(value)) {
          queryObj[key] = value[0] || '';
        }
      });

      setQuery(queryObj);
      setIsReady(true);
    };

    // Initial sync
    updateFromNextRouter();

    // Listen to Next.js router events
    const handleRouteChange = () => {
      // Clear the back-navigation flag shortly after the route settles, so the
      // saved-pagination guards (ProductProvider, BrowseCategoryPage) read the
      // correct direction during the post-navigation render.
      resetBackNavigationSoon();
      updateFromNextRouter();
    };

    // Resolve the navigation's direction at the moment it starts: `backNav` is
    // true only if a `popstate` fired just before this `routeChangeStart`. Must
    // be wired on start (not complete) so the post-navigation render reads the
    // correct, per-navigation value instead of a sticky global flag.
    nextRouter.events.on('routeChangeStart', markRouteChangeStart);
    nextRouter.events.on('routeChangeComplete', handleRouteChange);
    nextRouter.events.on('hashChangeComplete', handleRouteChange);

    return () => {
      nextRouter.events.off('routeChangeStart', markRouteChangeStart);
      nextRouter.events.off('routeChangeComplete', handleRouteChange);
      nextRouter.events.off('hashChangeComplete', handleRouteChange);
    };
  }, [nextRouter.isReady, nextRouter.pathname, nextRouter.asPath, nextRouter.query, nextRouter.events]);

  const push = (url: string, options?: TransitionOptions) => {
    void nextRouter.push(url, undefined, options);
  };

  const replace = (url: string, options?: TransitionOptions) => {
    void nextRouter.replace(url, undefined, options);
  };

  const prefetch = (url: string) => {
    // prefetch() rejects on network errors / when disabled; swallow so a warm
    // attempt never surfaces as an unhandled rejection.
    void nextRouter.prefetch(url).catch(() => { /* best-effort warm */ });
  };

  const value: RouterContextType = {
    pathname,
    asPath,
    query,
    push,
    replace,
    prefetch,
    isReady,
  };

  return (
    <RouterContext.Provider value={value}>
      {children}
    </RouterContext.Provider>
  );
};

export default RouterProvider;
